import React, { useEffect, useState, useContext, useCallback } from 'react';
import { FaPlus, FaCalculator, FaMemory, FaFileAlt } from 'react-icons/fa';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import Numbers from '~/utils/numbers';
import Dates from '~/utils/dates';
import './styles.css';

// Contexts
import { GeneralContext, ModalContext } from '~/contexts';
import { UnitsContext } from '~/contexts/forms/units';

// Modal Components
import AddPriceModal from './Modals/AddPriceModal';
import EditPriceModal from './Modals/EditPriceModal';
import DeletePriceModal from './Modals/DeletePriceModal';
import api from '~/services/api';
import MemoryCalculationModal from './Modals/MemoryCalculationModal';

const Form: React.FC<any> = ({unit, setReady, initialYear}) => {
    const [pricesData, setPricesData] = useState<any>(null);
    const [selectedYear, setSelectedYear] = useState<number>(typeof initialYear === 'number' ? initialYear : new Date().getFullYear());
    const [filteredPrices, setFilteredPrices] = useState<any>([]);
	const [isCalculating, setIsCalculating] = useState<boolean>(false);
	const [autoYearSet, setAutoYearSet] = useState<boolean>(typeof initialYear === 'number');

    const {user} = useContext(GeneralContext);
    // Contexts
    const Units = useContext(UnitsContext);
    const Modal = useContext(ModalContext);

    const errorMapper = {
        "Consumption Te ACL not found for unit_id:": "Ocorreu um erro para encontrar o consumo medido ou da TE/ACL (Necessario validar se existe valor para essa unidade nesse mes e ano)"
      } as const;
  
	// Mapeia detalhes técnicos do backend para mensagens claras ao usuário
	function mapErrorToUserMessage(err: any): string {
		const rawError: string = err?.response?.data?.error || err?.message || 'Erro desconhecido';
		const details: any = err?.response?.data?.details;
		const detailsArray: string[] = Array.isArray(details) ? details : [];
  
		// Preferir detalhes, mapeando por prefixos conhecidos
		for (const detail of detailsArray) {
			for (const key in errorMapper) {
				if (detail.includes(key)) {
					return (errorMapper as any)[key];
				}
			}
		}
  
		// Se houver detalhes mas sem mapeamento, mostrar o primeiro detalhe
		if (detailsArray.length > 0) {
			return detailsArray[0];
		}
  
		// Último fallback: tentar mapear a mensagem "error" genérica
		for (const key in errorMapper) {
			if (rawError.includes(key)) {
				return (errorMapper as any)[key];
			}
		}
  
		return rawError;
	}

    // Modal functions
    function openAddPriceModal() {
        Modal.setModalClass('mprices-add');
        Modal.setModalTitle('');
        Modal.setModalBody(
            <AddPriceModal
                unit_id={unit.id}
                unitContractPriceType={unit?.contract_price_type}
                defaultYear={selectedYear}
                onSave={handleAddPrice}
                onCancel={closeSubModal}
            />
        );
        Modal.setModalOpen(true);
    }

    function openEditPriceModal(price: any) {
        Modal.setModalClass('mprices-edit');
        Modal.setModalTitle('');
        Modal.setModalBody(
            <EditPriceModal
                price={price}
                onSave={handleEditPrice}
                onCancel={closeSubModal}
            />
        );
        Modal.setModalOpen(true);
    }

    function openDeletePriceModal(price: any) {
        Modal.setModalClass('mprices-delete');
        Modal.setModalTitle('');
        Modal.setModalBody(
            <DeletePriceModal
                price={price}
                onConfirm={() => handleDeletePrice(price)}
                onCancel={closeSubModal}
            />
        );
        Modal.setModalOpen(true);
    }

    function closeSubModal() {
        // Return to main prices modal
        Modal.setModalClass('mprices');
        Modal.setModalTitle('Editando Preços');
        Modal.setModalBody(<Form unit={unit} setReady={setReady} initialYear={selectedYear} />);
        Modal.setModalOpen(true);
    }

	type StatusKind = 'info' | 'success' | 'error';
  
	function showStatusModal(kind: StatusKind, title: string, message: string, details?: string[], showBackButton: boolean = true) {
		if (!Array.isArray(details) || details.length === 0) details = [];
		const kindColor: Record<StatusKind, string> = {
			info: '#333',
			success: '#2e7d32',
			error: '#d32f2f'
		};
  
		// Helpers de UI
		function BackButton() {
			return (
				<button className="btn-back" onClick={closeSubModal} title="Voltar">Voltar</button>
			);
		}
		function renderDetailsList(list?: string[]) {
			if (!Array.isArray(list) || list.length === 0) return null;
			return (
				<div style={{ color: '#666', fontSize: '14px' }}>
					<strong>Detalhes:</strong>
					<ul>
						{list.map((detail: string, index: number) => (
							<li key={index}>{detail}</li>
						))}
					</ul>
				</div>
			);
		}
  
		Modal.setModalClass('mmemory-calculation');
		Modal.setModalTitle(title);
		Modal.setModalBody(<>
			<div style={{ color: kindColor[kind], marginBottom: '10px' }}>
				<strong>{message}</strong>
			</div>
			{renderDetailsList(details)}
			{showBackButton && <BackButton />}
		</>);
		Modal.setModalOpen(true);
	}

    function formatPrice (value: string | number) {
        if (typeof value==='undefined') return '';
        else if (typeof value==='string') return Numbers.money(parseFloat(value.replace('.', '').replace(',', '.')), false, true);
        return Numbers.money(value, false, true);
    }

    function getMemoryCalculation(month: number) {
        const prelimUrl = `preliminary-reports/${unit?.id}/${String(selectedYear)}/${String(month)}`;

        api.get(prelimUrl, {
            headers: { Authorization: user.token }
        }).then((resp: any) => {
            const prelim = resp?.data?.data;
            Modal.setModalClass('mmemory-calculation');
            Modal.setModalTitle('Memória de cálculo');
            Modal.setModalBody(<MemoryCalculationModal inputData={prelim.inputData} totalValueFree={prelim.preliminaryReportFree.totalValue} totalValueCaptive={prelim.preliminaryReportCaptive.totalValue} />);
            Modal.setModalOpen(true);
		}).catch((err: any) => {
			console.error("Erro ao carregar memória de cálculo:", err);
			const userMessage = mapErrorToUserMessage(err);
			// Mostrar apenas a mensagem (sem lista de detalhes) e permitir voltar
			showStatusModal('error', 'Erro', userMessage, [], true);
            
            // Voltar para o modal da listagem de preços após 3 segundos
            setTimeout(() => {
                Modal.setModalClass('mprices');
                Modal.setModalTitle('Editando Preços');
                Modal.setModalBody(<Form unit={unit} setReady={setReady} initialYear={selectedYear} />);
                Modal.setModalOpen(true);
            }, 3000);
        });
    }

	async function generateGuaranteedDiscountPrice(month: number) {
		// Evitar concorrência de múltiplos cliques
		if (isCalculating) return;
  
		// Validações rápidas
		if (!unit?.id) {
			showStatusModal('error', 'Erro', 'Unidade inválida.', [], true);
			return;
		}
		if (!Number.isInteger(month) || month < 1 || month > 12) {
			showStatusModal('error', 'Erro', 'Mês inválido.', [], true);
			return;
		}
  
		const prelimUrl = `preliminary-reports/`;
  
		// Feedback inicial
		showStatusModal('info', 'Calculando preço', 'Estamos calculando o preço... aguarde um momento.', [], true);
  
		setIsCalculating(true);
		try {
			const payload = { unit_id: unit.id, year: selectedYear, month };
			const resp: any = await api.post(prelimUrl, payload, {
				headers: { Authorization: user.token }
			});
			// Atualiza dados da lista para refletir o novo preço calculado
			await getData();
  
			const resultValue = resp?.data?.data?.inputData?.price_billing_value.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
              });

			showStatusModal('success', 'Preço calculado', 'Preço calculado com sucesso! O relatório preliminar está disponível para visualização.', [ 'O resultado do preço calculado foi: R$ ' + resultValue ], true);
		} catch (err: any) {
			console.error('Erro ao calcular preço:', err);
			const userMessage = mapErrorToUserMessage(err);
			// Mostrar apenas a mensagem (sem lista de detalhes) e permitir voltar
			showStatusModal('error', 'Erro', userMessage, [], true);
		} finally {
			setIsCalculating(false);
		}
	}

    async function generatePreliminaryReportPriceFixed(month: number) {
       // Evitar concorrência de múltiplos cliques
		if (isCalculating) return;
  
		// Validações rápidas
		if (!unit?.id) {
			showStatusModal('error', 'Erro', 'Unidade inválida.', [], true);
			return;
		}
		if (!Number.isInteger(month) || month < 1 || month > 12) {
			showStatusModal('error', 'Erro', 'Mês inválido.', [], true);
			return;
		}
  
		const prelimUrl = `preliminary-reports/`;
  
		// Feedback inicial
		showStatusModal('info', 'Gerando relatório preliminar', 'Estamos gerando o relatório preliminar... aguarde um momento.', [], true);
  
		setIsCalculating(true);
		try {
			const payload = { unit_id: unit.id, year: selectedYear, month };
		    await api.post(prelimUrl, payload, {
				headers: { Authorization: user.token }
			});
			// Atualiza dados da lista para refletir o novo preço calculado
			await getData();
  
			showStatusModal('success', 'Relatório preliminar gerado', 'Relatório preliminar gerado com sucesso! O relatório preliminar está disponível para visualização.', [], true);
		} catch (err: any) {
			console.error('Erro ao gerar relatório preliminar:', err);
			const userMessage = mapErrorToUserMessage(err);
			// Mostrar apenas a mensagem (sem lista de detalhes) e permitir voltar
			showStatusModal('error', 'Erro', userMessage, [], true);
		} finally {
			setIsCalculating(false);
		}
    }

    function getMonthName(month: number) {
        month = Number(month);
        if (month===0) return 'Geral';
        return Dates._getNameMonth((month-1));
    }

    // Função para gerar todos os meses do ano com preços (para desconto garantido)
    function generateAllMonthsWithPrices() {
        const allMonths = [];
        for (let month = 1; month <= 12; month++) {
            const existingPrice = filteredPrices.find((price: any) => price.month === month);
            allMonths.push({
                month,
                price: existingPrice?.price || 0,
                price_type: existingPrice?.price_type || 'Calculado',
                type: existingPrice?.type || 'calculated',
                id: existingPrice?.id || null,
                unit_id: existingPrice?.unit_id || unit,
                year: selectedYear
            });
        }
        return allMonths;
    }

    // Função para gerar todos os meses para contrato fixo,
    // preenchendo com zeros quando não existir preço cadastrado
    function generateFullYearForFixedPrices() {
        const allMonths = [];
        for (let month = 1; month <= 12; month++) {
            const existingPrice = filteredPrices.find((price: any) => price.month === month);
            if (existingPrice) {
                allMonths.push(existingPrice);
            } else {
                allMonths.push({
                    month,
                    price: 0,
                    price_type: '-',
                    type: 'placeholder',
                    id: null,
                    unit_id: unit?.id,
                    year: selectedYear
                });
            }
        }
        return allMonths;
    }

    function getAvailableYears() {
        // Sempre mostrar ano atual e 4 anos anteriores
        const currentYear = new Date().getFullYear();
        const defaultYears = [];
        for (let i = currentYear; i >= currentYear - 4; i--) {
            defaultYears.push(i);
        }
        
        // Se não há dados, retornar apenas os anos padrão
        if (!pricesData || pricesData.length === 0) {
            return defaultYears;
        }
        
        // Combinar anos dos dados com anos padrão
        const dataYears = Array.from(new Set(pricesData.map((price: any) => price.year))) as number[];
        const allYears = Array.from(new Set([...defaultYears, ...dataYears])) as number[];
        
        return allYears.sort((a: number, b: number) => b - a); // Sort descending
    }

    function handleYearChange(event: React.ChangeEvent<HTMLSelectElement>) {
        const year = parseInt(event.target.value);
        setSelectedYear(year);
		setAutoYearSet(true);
    }

    const filterPricesByYear = useCallback((year: number) => {
        if (!pricesData) return [];
        return pricesData.filter((price: any) => price.year === year);
    }, [pricesData]);

    // Handle functions for modal actions
    async function handleAddPrice(newPriceData: any[]) {
        // Verificar duplicatas antes de salvar
        for (const item of newPriceData) {
            const exists = pricesData?.some((p: any) => p.year === item.year && p.month === item.month);
            if (exists) {
                const monthName = getMonthName(item.month);
                alert(`O preço já está cadastrado para ${monthName}/${item.year}. Selecione um mês diferente ou ajuste o período.`);
                return;
            }
        }
        // Salvar sequencialmente
        await Units.savePrices(newPriceData);
        await getData();
        closeSubModal();
    }

    async function handleEditPrice(editedPriceData: any) {
        const price = pricesData.find((price: any) => price.id === editedPriceData.id);

        if (!price) {
            alert('O preço não foi encontrado. Por favor, tente novamente.');
            return;
        }
        if (price.year !== editedPriceData.year || price.month !== editedPriceData.month) {
            alert('O ano ou mês informado não é o mesmo do preço. Por favor, tente novamente.');
            return;
        }
        await Units.savePrices([editedPriceData]);
        await getData();
        closeSubModal();

    }

    async function handleDeletePrice(priceToDelete: any) {
            const updatedPrices = pricesData.filter((price: any) => 
                !(price.year === priceToDelete.year && 
                price.month === priceToDelete.month && 
                price.unit_id === priceToDelete.unit_id)
            );
            setPricesData(updatedPrices);
            await Units.deletePrices(priceToDelete.id);
            await getData();
            // preço é deletado visualmente para o usuario e apos isso é salvo no banco de dados
        closeSubModal();
    }

    const getData = useCallback(async () => {   
        if (!unit || !unit.id) {
            setPricesData([]);
            return;
        }
        await api.get(`/clients/prices/${unit.id}`, {
            headers: { Authorization: user.token }
        }).then((resp: any) => {
            // Definir o ano mais recente como padrão se houver dados
            if (resp.data && resp.data.length > 0) {
                const years = resp.data.map((price: any) => price.year);
                const uniqueYears = Array.from(new Set(years)) as number[];
                const mostRecentYear = Math.max(...uniqueYears);
                const currentYear = new Date().getFullYear();
                
                // Priorizar ano atual se houver dados, senão o mais recente
                const preferredYear = uniqueYears.includes(currentYear) ? currentYear : mostRecentYear;
                
                // Ajuste de ano automático apenas uma vez, para não sobrescrever seleção do usuário
                if (!autoYearSet && preferredYear !== selectedYear) {
                    setSelectedYear(preferredYear);
					setAutoYearSet(true);
                }
            }
            
            setPricesData(resp.data);
        }).catch(err => {
            console.error("Erro ao carregar preços:", err);
        })
    }, [unit, user.token, selectedYear, autoYearSet])

    useEffect(() => {
        getData();
    }, [unit, getData])

    // Update filtered prices when year changes or data changes
    useEffect(() => {
        if (pricesData) {
            const filtered = filterPricesByYear(selectedYear);
            // Ordenar por mês de forma decrescente para exibição de preço fixo
            const sorted = [...filtered].sort((a: any, b: any) => {
                const ma = Number(a?.month) || 0;
                const mb = Number(b?.month) || 0;
                return mb - ma;
            });
            setFilteredPrices(sorted);
        }
    }, [pricesData, selectedYear, filterPricesByYear])

    return (
        pricesData && (
            <div className="prices-container">
                <div className="prices-header">
                    <h3>Preços: {unit?.contract_price_type === 'guaranteedDiscount' ? 'Desconto garantido' : 'Fixo'}</h3>
                    <div className="header-controls">
                        <div className="year-filter">
                            <select
                                value={selectedYear}
                                onChange={handleYearChange}
                                className="year-select"
                            >
                                {getAvailableYears().map((year: number) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>


                <div className="prices-table">
                    <div className="table-header">
                        <div className="col-month">Mês</div>
                        <div className="col-price">Valor</div>
                        <div className="col-source">Origem</div>
                        <div className="col-actions">Ações</div>
                    </div>

                    <div className="table-body">
                        {unit?.contract_price_type === 'guaranteedDiscount' ? (
                            generateAllMonthsWithPrices().map((price: any) => {
                                const priceId = price.id || price.unit_id + '_' + price.year + '_' + price.month;
                                return (
                                    <div key={priceId} className="table-row">
                                        <div className="col-month">
                                            <span>{getMonthName(price.month)}</span>
                                        </div>
                                        <div className="col-price">
                                            <span>R$ {formatPrice(price.price || '0.00')}</span>
                                        </div>
                                        <div className="col-source">
                                            <span className={`type-badge ${price.type?.toLowerCase()}`}>
                                                {price.price_type === 'calculated' ? 'Calculado' : price.price_type === 'manual' ? 'Manual' : ''}
                                            </span>
                                        </div>
                                        <div className="col-actions">
                                            <div className="action-buttons">
                                                <button 
                                                    className="btn-edit" 
                                                    onClick={() => generateGuaranteedDiscountPrice(price.month)}
                                                    title="Calcular"
                                                    disabled={isCalculating}
                                                >
                                                    <FaCalculator size={16} />
                                                </button>
                                                <button 
                                                    className="btn-view" 
                                                    onClick={() => getMemoryCalculation(price.month)}
                                                    title="Memoria de cálculo"
                                                    disabled={price.price_type !== 'calculated'}
                                                >
                                                    <FaMemory size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            generateFullYearForFixedPrices().map((price: any) => {
                                const priceId = price.id || ((unit?.id || 'unit') + '_' + price.year + '_' + price.month);
                                return (
                                    <div key={priceId} className="table-row">
                                        <div className="col-month">
                                            <span>{getMonthName(price.month)}</span>
                                        </div>
                                        <div className="col-price">
                                            <span>R$ {formatPrice(price.price || '0.00')}</span>
                                        </div>
                                        <div className="col-source">
                                            <span className={`type-badge ${price.type?.toLowerCase()}`}>
                                                {price.price_type}
                                            </span>
                                        </div>
                                        <div className="col-actions">
                                            <div className="action-buttons">
                                                {price.id ? (
                                                    <>
                                                        <button 
                                                            className="btn-edit" 
                                                            onClick={() => openEditPriceModal(price)}
                                                            title="Editar"
                                                        >
                                                            <FiEdit2 size={16} />
                                                        </button>
                                                        <button 
                                                            className="btn-delete" 
                                                            onClick={() => openDeletePriceModal(price)}
                                                            title="Deletar"
                                                        >
                                                            <FiTrash2 size={16} />
                                                        </button>
                                                        <button 
                                                            className="btn-generate" 
                                                            onClick={() => generatePreliminaryReportPriceFixed(price.month)}
                                                            title="Gerar relatório preliminar"
                                                            disabled={price.price === 0 || price.price === null}
                                                        >
                                                        <FaFileAlt size={16} color="#2E7D32"/>
                                                        </button>
                                                        <button 
                                                            className="btn-view" 
                                                            onClick={() => getMemoryCalculation(price.month)}
                                                            title="Memoria de cálculo"
                                                        >
                                                            <FaMemory size={16} />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button 
                                                        className="btn-edit" 
                                                        onClick={openAddPriceModal}
                                                        title="Adicionar"
                                                    >
                                                        <FaPlus size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
        )
    )
}

export default Form;